/**
 * AvatarUpload - Profile photo upload with square crop tool.
 *
 * Provides a Facebook-style avatar editing experience:
 *	 1. User clicks their current avatar or a "Change Photo" button
 *	 2. File picker opens (accepts image/*)
 *	 3. A crop modal appears with the selected image and a circular crop area
 *	 4. User pans/zooms to frame their face within the circle
 *	 5. On "Save", the image is cropped to a square, compressed to max 1500x1500,
 *		and returned as a base64 JPEG for upload
 *
 * Uses react-easy-crop for the crop UI and browser-image-compression for
 * final compression after cropping.
 *
 * @example
 * <AvatarUpload
 *	   currentPhotoUrl={user.profilePhotoUrl}
 *	   userName="John Doe"
 *	   onSave={(base64) => uploadAvatar(base64)}
 * />
 */
import React, { useState, useRef, useCallback } from 'react';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import { compressPhoto } from '../../../utils/imageCompression';

export interface AvatarUploadProps {
	/** Current profile photo URL — shown as the default avatar */
	currentPhotoUrl?: string | null;
	/** User's name — first initial used as fallback when no photo */
	userName: string;
	/** Called with the cropped + compressed base64 JPEG data URL */
	onSave: (base64: string) => Promise<void>;
	/** Size of the avatar circle in px (default 96) */
	size?: number;
	/** Optional extra classes for the avatar display wrapper */
	className?: string;
}

/**
 * Creates a canvas-cropped image from the source image and crop area.
 * Returns a Blob of the cropped region as JPEG.
 */
const getCroppedImage = (imageSrc: string, cropArea: Area): Promise<Blob> => {
	return new Promise((resolve, reject) => {
		const image = new Image();
		image.crossOrigin = 'anonymous';
		image.onload = () => {
			const canvas = document.createElement('canvas');
			// Output square at the cropped dimensions (will be compressed after)
			canvas.width = cropArea.width;
			canvas.height = cropArea.height;
			const ctx = canvas.getContext('2d');
			if (!ctx) return reject(new Error('Canvas context unavailable'));

			// Draw the cropped region onto the canvas
			ctx.drawImage(
				image,
				cropArea.x, cropArea.y,		  // source x, y
				cropArea.width, cropArea.height, // source width, height
				0, 0,							// destination x, y
				cropArea.width, cropArea.height	 // destination width, height
			);

			canvas.toBlob(
				(blob) => {
					if (blob) resolve(blob);
					else reject(new Error('Canvas toBlob failed'));
				},
				'image/jpeg',
				0.92 // JPEG quality
			);
		};
		image.onerror = () => reject(new Error('Failed to load image'));
		image.src = imageSrc;
	});
};

const AvatarUpload: React.FC<AvatarUploadProps> = ({ currentPhotoUrl, userName, onSave, size = 96, className }) => {
	// --- State ---
	const [imageSrc, setImageSrc] = useState<string | null>(null);	// Raw image for cropping
	const [crop, setCrop] = useState({ x: 0, y: 0 });				// Pan position
	const [zoom, setZoom] = useState(1);							 // Zoom level
	const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null); // Final crop area
	const [isCropModalOpen, setIsCropModalOpen] = useState(false);	 // Modal visibility
	const [isSaving, setIsSaving] = useState(false);				 // Upload in progress

	/* Freeze the page behind the crop modal — dragging to pan the crop
	   must not scroll the profile page underneath */
	useBodyScrollLock(isCropModalOpen && !!imageSrc);
	const [saveError, setSaveError] = useState<string | null>(null); // Upload error message
	const fileInputRef = useRef<HTMLInputElement>(null);

	// First initial for fallback avatar
	const initial = userName?.charAt(0)?.toUpperCase() || '?';

	/**
	 * Handle file selection — read the file and open the crop modal.
	 */
	const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		// Read the selected file as a data URL for the crop preview
		const reader = new FileReader();
		reader.onload = () => {
			setImageSrc(reader.result as string);
			setCrop({ x: 0, y: 0 });
			setZoom(1);
			setIsCropModalOpen(true);
		};
		reader.readAsDataURL(file);

		// Reset input so the same file can be re-selected if needed
		e.target.value = '';
	}, []);

	/**
	 * Called by react-easy-crop when the user finishes adjusting the crop area.
	 * Stores the pixel coordinates of the cropped region.
	 */
	const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPx: Area) => {
		setCroppedAreaPixels(croppedAreaPx);
	}, []);

	/**
	 * Process the crop: extract the cropped region, compress to max 1500x1500,
	 * convert to base64, and call onSave.
	 */
	const handleSaveCrop = useCallback(async () => {
		if (!imageSrc || !croppedAreaPixels) return;

		setSaveError(null);
		setIsSaving(true);
		try {
			// Step 1: Crop the image to the selected square region
			const croppedBlob = await getCroppedImage(imageSrc, croppedAreaPixels);

			// Step 2: Compress through the shared pipeline, which also gives
			// us the base64 for free (it reads the compressed blob once) and
			// brings the canvas fallback along for old mobile WebViews.
			//
			// 512px / 0.5MB, not the previous 1500px / 2MB: the avatar renders at
			// 96px (profile) and 50px (nav thumbnail), so 512 is already 5x the
			// largest display size. The old ceiling produced a ~2MB JPEG, which
			// base64 inflates by a third — big enough to be rejected by a proxy's
			// default request body limit and to stall an upload on mobile data.
			const croppedFile = new File([croppedBlob], 'avatar.jpg', { type: 'image/jpeg' });
			const { dataUrl } = await compressPhoto(croppedFile, {
				maxWidthOrHeight: 512,
				maxSizeMB: 0.5,
			});

			// Step 3: Pass to parent for upload
			await onSave(dataUrl);

			// Close modal on success
			setIsCropModalOpen(false);
			setImageSrc(null);
		} catch (err) {
			console.error('Avatar upload failed:', err);
			// Surface the failure so the user isn't left thinking it saved —
			// the modal stays open with a visible error instead of silently
			// reverting the button from "Saving..." to "Save Photo".
			setSaveError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
		} finally {
			setIsSaving(false);
		}
	}, [imageSrc, croppedAreaPixels, onSave]);

	/**
	 * Cancel cropping — close modal and discard the selected image.
	 */
	const handleCancel = useCallback(() => {
		setIsCropModalOpen(false);
		setImageSrc(null);
		setSaveError(null);
		setCrop({ x: 0, y: 0 });
		setZoom(1);
	}, []);

	return (
		<>
			{/* --- Avatar display + change button --- */}
			<div className={`avatar-upload flex flex-col items-center gap-8px ${className ?? ''}`} data-ref="organism-avatar-upload">
				{/* Clickable avatar — opens file picker */}
				<button type="button" onClick={() => fileInputRef.current?.click()} className="relative group cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 rounded-full" style={{ width: size, height: size }} aria-label="Change profile photo">
					{currentPhotoUrl ? (
						<img src={currentPhotoUrl} alt="" className="rounded-full object-cover w-full h-full" />
					) : (
						<div className="rounded-full bg-brand-primary text-white flex items-center justify-center font-heading font-bold w-full h-full" style={{ fontSize: size * 0.4 }}>
							{initial}
						</div>
					)}
					{/* Hover overlay with camera icon */}
					<div className="absolute inset-0 rounded-full bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
						<svg className="w-24px h-24px text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
					</div>
				</button>

				{/* "Change Photo" text link */}
				<button type="button" onClick={() => fileInputRef.current?.click()} className="avatar-upload__change-link inline-flex items-center min-h-44px text-14px font-sans text-brand-primary hover:text-brand-primary-hover transition-colors">
					Change Photo
				</button>

				{/* Hidden file input */}
				<input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" aria-hidden="true" />
			</div>

			{/* --- Crop Modal --- */}
			{isCropModalOpen && imageSrc && (
				<div className="avatar-upload__modal fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-60" role="dialog" aria-modal="true" aria-label="Crop profile photo">
					<div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-16px max-h-[90vh] flex flex-col overflow-hidden">
						{/* Modal header */}
						<div className="px-24px py-16px border-b border-brand-border flex items-center justify-between flex-shrink-0">
							<h3 className="text-18px font-heading font-bold text-brand-text">Crop Photo</h3>
							<button type="button" onClick={handleCancel} className="text-gray-400 hover:text-gray-600 min-w-44px min-h-44px flex items-center justify-center" aria-label="Close">
								<svg className="w-20px h-20px" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
							</button>
						</div>

						{/* Crop area — fixed aspect ratio 1:1, circular crop shape.
						    flex-1 with a min/max keeps the crop usable while letting
						    the modal stay within 90vh so the action buttons remain
						    reachable on short/mobile screens. */}
						<div className="relative w-full flex-1 min-h-[220px] max-h-[400px]">
							<Cropper
								image={imageSrc}
								crop={crop}
								zoom={zoom}
								aspect={1}
								cropShape="round"
								showGrid={false}
								onCropChange={setCrop}
								onZoomChange={setZoom}
								onCropComplete={onCropComplete}
							/>
						</div>

						{/* Zoom slider */}
						<div className="px-24px py-12px flex items-center gap-12px flex-shrink-0">
							<svg className="w-16px h-16px text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" /></svg>
							<input type="range" min={1} max={3} step={0.05} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="flex-1 accent-brand-primary" aria-label="Zoom" />
							<svg className="w-16px h-16px text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" /></svg>
						</div>

						{/* Action buttons */}
						{saveError && (
							<p role="alert" className="px-24px pb-8px text-13px text-red-600 flex-shrink-0">{saveError}</p>
						)}
						<div className="px-24px py-16px border-t border-brand-border flex justify-end gap-12px flex-shrink-0">
							<button type="button" onClick={handleCancel} disabled={isSaving} className="px-16px py-8px text-14px font-sans font-medium text-brand-text-muted border border-brand-border rounded-md hover:bg-brand-surface transition-colors min-h-44px">
								Cancel
							</button>
							<button type="button" onClick={handleSaveCrop} disabled={isSaving} className="px-24px py-8px text-14px font-sans font-medium text-white bg-brand-primary rounded-md hover:bg-brand-primary-hover transition-colors disabled:opacity-50 min-h-44px flex items-center gap-8px">
								{isSaving ? (
									<>
										<svg className="w-16px h-16px animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
										Saving...
									</>
								) : 'Save Photo'}
							</button>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default AvatarUpload;
